import type { ReactNode } from "react";
import { Link } from "react-router";
import { Copy, ExternalLink, History, Star, UserRound } from "lucide-react";
import { paths } from "../../app/paths.js";
import type { Entry } from "../../lib/entries.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { displayHost, safeHttpUrl } from "../../lib/url.js";
import { Avatar, Card, CardHeader, IconButton, Skeleton } from "../../ui/primitives.js";
import { useEntryActions } from "../vault/useEntryActions.js";
import type { EntryActions } from "../vault/useEntryActions.js";

function EntryQuickButtons(props: { readonly entry: Entry; readonly actions: EntryActions; readonly compact?: boolean | undefined }): ReactNode {
  const { entry, actions } = props;
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      {props.compact !== true ? (
        <IconButton
          label={`Webadresse von ${entry.data.title} öffnen`}
          icon={ExternalLink}
          size="sm"
          disabled={safeHttpUrl(entry.data.url) === undefined}
          onClick={(): void => actions.openUrl(entry)}
        />
      ) : null}
      <IconButton
        label={`Benutzernamen für ${entry.data.title} kopieren`}
        icon={UserRound}
        size="sm"
        disabled={entry.data.username === ""}
        onClick={(): void => actions.copyUsername(entry)}
      />
      <IconButton
        label={`Passwort für ${entry.data.title} kopieren`}
        icon={Copy}
        size="sm"
        disabled={entry.data.password === ""}
        onClick={(): void => actions.copyPassword(entry)}
      />
    </div>
  );
}

/** Favorites as one-click tiles: copy username/password or open the site straight from the dashboard. */
export function FavoritesPanel(props: { readonly favorites: ReadonlyArray<Entry>; readonly total: number; readonly loading: boolean }): ReactNode {
  const actions = useEntryActions();
  return (
    <Card className="flex h-full flex-col overflow-hidden">
      <CardHeader
        title="Schnellzugriff"
        description="Deine Favoriten – kopieren, ohne den Eintrag zu öffnen."
        icon={Star}
        action={
          props.total > props.favorites.length ? (
            <Link to="/vault?view=favorites" className="whitespace-nowrap text-sm text-mist transition-colors hover:text-flow">
              Alle {props.total}
            </Link>
          ) : undefined
        }
      />
      {props.loading ? (
        <div className="grid gap-3 p-5 sm:grid-cols-2">
          {[0, 1, 2, 3].map((i: number) => (
            <Skeleton key={i} className="h-[4.25rem]" />
          ))}
        </div>
      ) : props.favorites.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center">
          <span className="grid size-10 place-items-center rounded-xl border border-line bg-ink-3 text-flow">
            <Star size={18} aria-hidden="true" />
          </span>
          <p className="text-sm font-medium text-foam">Noch keine Favoriten</p>
          <p className="max-w-xs text-sm text-mist">Markiere oft genutzte Einträge mit dem Stern – sie erscheinen dann hier zum Kopieren mit einem Klick.</p>
        </div>
      ) : (
        <ul className="grid gap-3 p-5 sm:grid-cols-2">
          {props.favorites.map((entry: Entry) => {
            const host = displayHost(entry.data.url);
            return (
              <li key={entry.meta.id} className="group flex items-center gap-3 rounded-lg border border-line bg-ink py-2.5 pl-3 pr-1.5 transition-colors hover:border-line-strong">
                <Link to={paths.vaultItem(entry.meta.id)} className="flex min-w-0 flex-1 items-center gap-3 rounded-md outline-offset-4">
                  <Avatar label={entry.data.title} />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foam group-hover:text-flow-bright">{entry.data.title}</span>
                    <span className="block truncate text-xs text-mist">{host ?? (entry.data.username !== "" ? entry.data.username : entry.meta.folder)}</span>
                  </span>
                </Link>
                <EntryQuickButtons entry={entry} actions={actions} />
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export interface RecentItem {
  readonly entry: Entry;
  readonly at: number;
}

/** Entries the user copied from or opened lately on this device. */
export function RecentlyUsedPanel(props: { readonly items: ReadonlyArray<RecentItem>; readonly now: number; readonly loading: boolean }): ReactNode {
  const actions = useEntryActions();
  return (
    <Card className="flex h-full flex-col overflow-hidden">
      <CardHeader title="Zuletzt verwendet" description="Auf diesem Gerät kopiert oder geöffnet." icon={History} />
      {props.loading ? (
        <div className="space-y-2 p-5">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : props.items.length === 0 ? (
        <p className="flex flex-1 items-center px-5 py-8 text-sm text-mist">
          Sobald du ein Passwort kopierst oder eine Webadresse öffnest, findest du den Eintrag hier wieder.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {props.items.map((item: RecentItem) => (
            <li key={item.entry.meta.id} className="flex items-center gap-3 py-1.5 pl-5 pr-2.5 transition-colors hover:bg-foam/[0.03]">
              <Link to={paths.vaultItem(item.entry.meta.id)} className="flex min-w-0 flex-1 items-center gap-3 py-1">
                <Avatar label={item.entry.data.title} size="sm" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foam">{item.entry.data.title}</span>
                  <span className="block truncate text-xs text-mist-2" title={formatDateTime(item.at)}>
                    {formatRelativeTime(item.at, props.now)}
                  </span>
                </span>
              </Link>
              <EntryQuickButtons entry={item.entry} actions={actions} compact />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
